// Thumbnail (preview) image input.
// Shows a plus sign until an image is chosen, then the image itself. Clicking
// the image opens it full size; the X removes it (a newly chosen file falls
// back to the recipe's existing preview, if any; removing the existing preview
// tells the parent so it can ask the API to delete it on save).
//
// The chosen file is owned by the parent (`image`/`setImage`) so it can also
// be set from elsewhere, e.g. "Use as preview image" on a gallery image.

import { useEffect, useRef, useState } from "react";
import { Button, Image } from "react-bootstrap";
import { PlusCircle, XCircle } from "react-bootstrap-icons";
import ImagePreviewModal from "../ImagePreviewModal";

type ThumbnailInputProp = {
  image: File | null;
  setImage: React.Dispatch<React.SetStateAction<File | null>>;
  existingUrl?: string | null;
  onRemoveExisting?: () => void;
};

const ThumbnailInput = ({ image, setImage, existingUrl, onRemoveExisting }: ThumbnailInputProp) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imagePreviewURL, setImagePreviewURL] = useState("");
  const [viewing, setViewing] = useState(false);

  // One object URL per chosen file, revoked when the file changes or goes away.
  useEffect(() => {
    if (!image) {
      setImagePreviewURL("");
      return;
    }
    const url = URL.createObjectURL(image);
    setImagePreviewURL(url);
    return () => URL.revokeObjectURL(url);
  }, [image]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImage(file);
    // allow picking the same file again after removing it
    e.target.value = "";
  };

  const remove = () => {
    if (image) {
      setImage(null);
    } else {
      onRemoveExisting?.();
    }
  };

  const shown = imagePreviewURL || existingUrl || "";

  return (
    <div className="thumbnailInput recipeImage recipeImageThumbnailHolder">
      <input
        name="preview_image"
        type="file"
        accept="image/*"
        ref={fileInputRef}
        style={{ display: "none" }}
        onChange={handleFileChange}
      ></input>
      {shown ? (
        <>
          <button
            type="button"
            className="imageViewButton"
            aria-label="View preview image full size"
            title="Click to view full size"
            onClick={() => setViewing(true)}
          >
            <Image src={shown} alt="Recipe preview" draggable={false}></Image>
          </button>
          <div className="galleryActions">
            <Button
              type="button"
              variant="light"
              aria-label="Remove preview image"
              title="Remove the preview image"
              onClick={remove}
            >
              <XCircle />
            </Button>
          </div>
          <ImagePreviewModal src={viewing ? shown : null} alt="Recipe preview, full size" onHide={() => setViewing(false)} />
        </>
      ) : (
        <button
          type="button"
          className="imageViewButton"
          aria-label="Add a preview image"
          title="Click to add a preview image"
          onClick={() => fileInputRef.current?.click()}
        >
          <PlusCircle className="recipeImageAddIcon" />
        </button>
      )}
    </div>
  );
};

export default ThumbnailInput;
